import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  Table,
  InputNumber,
  Select,
  Button,
  Breadcrumb,
  Space,
  DatePicker,
  Form,
  Row,
  Col,
} from 'antd';
import { useNavigate } from 'react-router-dom';
import { HomeOutlined } from '@ant-design/icons';

import { apiRequest } from '../../../../service/apiRequest';
import { Wood } from '../../../../types/Wood';
import { showSuccess, showError } from '../../../../components/errorHandler';
import { HPO, POItem } from '../../../../types/HPO'; // HPO type includes po_details[]
const { Option } = Select;
type WoodRow = {
  key: string;
  woodId: number | null;
  wood_type_id: number | null;
  widths: { [width: number]: number };
  pbg_cft: number;
  wood_type_name: string;
  rate: number;
};

const woodWidths = Array.from({ length: 18 }, (_, i) => i + 3); // 3 to 20 inches

const PartyNapi: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const tableRef = useRef<HTMLDivElement | null>(null);

  const [wooddata, setWoodData] = useState<Wood[]>([]);
  const [billdata, setBillData] = useState<HPO | null>(null);
  const [dataSource, setDataSource] = useState<WoodRow[]>([]);

  // Initialize table rows from billdata.po_details
  useEffect(() => {
    if (billdata?.po_details?.length) {
      const rows: WoodRow[] = billdata.po_details.map((item, index) => ({
        key: (index + 1).toString(),
        woodId: item.wood_id ?? null,
        wood_type_id: item.wood_type_id ?? null,
        pbg_cft: item.pbg_cft ?? 0,
        wood_type_name: item?.wood_type_name ?? null,
        widths: woodWidths.reduce(
          (acc, w) => ({ ...acc, [w]: item.widths?.[w] ?? 0 }),
          {}
        ),
        rate: item.pbg_rate ?? 0,
      }));
      setDataSource(rows);
    }
  }, [billdata]);

  // Fetch wood list
  const fetchWood = async () => {
    try {
      const data = await apiRequest<Wood[]>({
        method: 'get',
        url: `woods`,
      });
      setWoodData(data);
    } catch (error) {
      showError('Failed to fetch Wood');
      console.error(error);
    }
  };

  // Fetch PO bill
  const fetchBill = async (id: string) => {
    try {
      const params = new URLSearchParams({ id });
      const data = await apiRequest<HPO>({
        method: 'get',
        url: `get-po-bill?${params.toString()}`,
      });
      setBillData(data);
    } catch (error) {
      showError('Failed to fetch PO Bill');
      console.error(error);
    }
  };

  useEffect(() => {
    if (id) {
      fetchBill(id); // safe, id is string
    }
    fetchWood();
  }, [id]);



  // Update woodId
  const handleWoodSizeChange = (value: number | null, key: string) => {
    setDataSource((prev) =>
      prev.map((row) => (row.key === key ? { ...row, woodId: value } : row))
    );
  };

  // Update width count
  const handleWidthChange = (value: number | null, key: string, width: number) => {
    setDataSource((prev) =>
      prev.map((row) =>
        row.key === key
          ? { ...row, widths: { ...row.widths, [width]: value ?? 0 } }
          : row
      )
    );
  };

  // Update rate
  const handleRateChange = (value: number | null, key: string) => {
    setDataSource((prev) =>
      prev.map((row) => (row.key === key ? { ...row, rate: value ?? 0 } : row))
    );
  };

  // Scroll input into view
  const handleInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    const input = e.currentTarget;
    const container = tableRef.current;
    if (!container) return;

    const containerRect = container.getBoundingClientRect();
    const inputRect = input.getBoundingClientRect();

    // Scroll if input is outside container view
    if (inputRect.right > containerRect.right) {
      container.scrollLeft += inputRect.right - containerRect.right + 20;
    } else if (inputRect.left < containerRect.left) {
      container.scrollLeft -= containerRect.left - inputRect.left + 20;
    }
  };

  // Utility: calculate total pieces
  const calculateTotalPcs = (widths: { [width: number]: number }) =>
    Object.values(widths).reduce((acc, val) => acc + val, 0);

  // Utility: calculate CFT
  const calculateCFT = (
    woodId: number | null,
    widths: { [width: number]: number },
    woodData: Wood[]
  ) => {
    if (!woodId) return 0;
    const wood = woodData.find((w) => w.wood_id === woodId);
    if (!wood) return 0;

    const length = Number(wood.wood_length ?? 0);
    const thickness = Number(wood.wood_thickness ?? 0);

    const widthQtyTotal = Object.entries(widths).reduce(
      (sum, [w, q]) => sum + Number(w) * (q ?? 0),
      0
    );

    const cft = (widthQtyTotal * ((length / 12) * thickness)) / 144;
    return parseFloat(cft.toFixed(4));
  };

  // Calculate amount
  const calculateAmount = (cft: number, rate: number) =>
    parseFloat((cft * rate).toFixed(4));

  const hasAtLeastOneWidth = (widths: { [width: number]: number }) =>
    Object.values(widths).some(qty => qty && qty > 0);
  // Submit handler
  const handleSubmit = async () => {
    // ❌ Validation: wood not selected
    const invalidWood = dataSource.find(row => !row.woodId);
    if (invalidWood) {
      showError('Please select wood for all rows');
      return;
    }

    // ❌ Validation: no width entered
    const invalidWidths = dataSource.find(
      row => !hasAtLeastOneWidth(row.widths)
    );

    if (invalidWidths) {
      showError('Each row must have at least one width quantity');
      return;
    }
    const payload = {
      po_id: id,
      woodDetails: dataSource.map((row) => ({
        woodId: row.woodId,
        widths: row.widths,
        rate: row.rate,
        wood_type_id: row.wood_type_id,
      })),
    };
    try {
      const res = await apiRequest({
        method: 'post',
        url: 'insert-party-napi',
        data: payload,
      });
      showSuccess('Data submitted successfully!');
      navigate(`/self-napi/${id}`);
    } catch (error) {
      showError('Failed to submit data');
      console.error(error);
    }
  };
  const isDifferent = (a: number, b: number, precision = 2) =>
    Number(a.toFixed(precision)) !== Number(b.toFixed(precision));
  // Table columns
  const columns = [
    {
      title: 'Description',
      dataIndex: 'woodSize',
      key: 'woodSize',
      fixed: 'left' as const,
      width: 120,
      render: (_: any, record: WoodRow) => (
        <>
          {record.wood_type_name}
          <Select
            value={record.woodId}
            onChange={(val) => handleWoodSizeChange(val, record.key)}
            placeholder="Select wood"
            options={wooddata.map((w) => ({ label: w.wood_name, value: w.wood_id }))}
            allowClear
          />
        </>
      ),
    },
    ...woodWidths.map((width) => ({
      title: `${width}"`,
      dataIndex: `width-${width}`,
      key: `width-${width}`,
      width: 80,
      render: (_: any, record: WoodRow) => (
        <InputNumber
          min={0}
          value={record.widths[width]}
          onChange={(val) => handleWidthChange(val, record.key, width)}
          style={{ width: 70 }}
          onFocus={handleInputFocus}
        />
      ),
    })),
    {
      title: 'Total Pcs',
      key: 'totalPcs',
      fixed: 'right' as const,
      width: 100,
      render: (_: any, record: WoodRow) => calculateTotalPcs(record.widths),
    },
    {
      title: 'CFT',
      key: 'cft',
      fixed: 'right' as const,
      width: 140,
      render: (_: any, record: WoodRow) => {
        const calculatedCft = calculateCFT(
          record.woodId,
          record.widths,
          wooddata
        );

        const hasDiff =
          record?.pbg_cft !== undefined &&
          isDifferent(calculatedCft, record.pbg_cft, 3);

        return (
          <div style={{ color: hasDiff ? 'red' : 'inherit' }}>
            <strong>{calculatedCft}</strong>
            {record?.pbg_cft !== undefined && (
              <div style={{ fontSize: 11 }}>
                Bill CFT: {record.pbg_cft}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Rate',
      key: 'rate',
      fixed: 'right' as const,
      width: 100,
      render: (_: any, record: WoodRow) => (
        <span>Rs. {record.rate}</span>
      )
    },
    {
      title: 'Amount',
      key: 'amount',
      fixed: 'right' as const,
      width: 180,
      render: (_: any, record: WoodRow) => {
        const calculatedCft = calculateCFT(
          record.woodId,
          record.widths,
          wooddata
        );

        const calculatedAmount = calculateAmount(
          calculatedCft,
          record.rate
        );

        const billAmount =
          record?.pbg_cft !== undefined
            ? record.pbg_cft * record.rate
            : undefined;

        const hasDiff =
          billAmount !== undefined &&
          isDifferent(calculatedAmount, billAmount, 2);

        return (
          <div style={{ color: hasDiff ? 'red' : 'inherit' }}>
            <strong>Rs. {calculatedAmount}</strong>
            {billAmount !== undefined && (
              <div style={{ fontSize: 11 }}>
                Bill Amount: Rs. {billAmount.toFixed(4)}
              </div>
            )}
          </div>
        );
      },
    },

  ];

  return (
    <>
      <Space
        style={{
          display: 'flex',
          marginBottom: 16,
          alignItems: 'center',
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        <Breadcrumb style={{ display: 'inline-block', margin: 0 }}>
          <Breadcrumb.Item href="/">
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>Party NAPI</Breadcrumb.Item>
        </Breadcrumb>
      </Space>

      <div style={{ padding: 20 }}>
        <h2>Purchase Order Detail</h2>

        <Form layout="vertical">
          <Row gutter={16} align="middle">
            <Col span={16}>
              <span>
                <strong>Company Name:</strong> {billdata?.ledger_company_name}{' '}
                {billdata?.ledger_gstin && `(GSTIN: ${billdata?.ledger_gstin})`}
              </span>
            </Col>
            <Col span={8}>
              <strong>Challan No.:</strong> <span>{billdata?.pbill_challan_no}</span>
            </Col>
          </Row>
        </Form>

        <div ref={tableRef} style={{ overflowX: 'auto', marginTop: 20 }}>
          <Table
            columns={columns}
            dataSource={dataSource}
            pagination={false}
            scroll={{ x: 'max-content' }}
            bordered
            size="small"
          />
        </div>

        <div style={{ marginTop: 20 }}>
          <Button type="primary" onClick={handleSubmit}>
            Submit
          </Button>
        </div>
      </div>
    </>
  );
};

export default PartyNapi;
